import { HierarchyRectangularNode } from '@visx/hierarchy/lib/types';
import { useMemo, type JSX } from 'react';

import { LABELS_DISPLAY } from '../constants.js';
import { TreeMapSquareRenderer } from './canvas/TreeMapSquareRenderer.js';
import type { LegendItem } from '../../core/components/legend/categorical/types/legend.js';
import { useLabelsDisplay } from '../context/LabelsDisplayContext.js';
import { useSetHierarchyNodes } from '../hooks/useSetHierarchyNodes.js';
import {
  type HighlightingMap,
  type InternalTreeMapNode,
} from '../types/internal.js';
import { getNodeColor } from '../utils/get-node-color.js';
import { getNodeDimension } from '../utils/get-node-dimension.js';
import { getNodeOpacity } from '../utils/get-node-opacity.js';
import { getNodePosition } from '../utils/get-node-position.js';
import { shouldDrawLeafNode } from '../utils/should-draw-leaf-node.js';
import { splitNodesByDepth } from '../utils/split-nodes-by-depth.js';
import { withParentLabel } from '../utils/with-parent-label.js';

interface TreeMapCanvasProps {
  treeMapData: HierarchyRectangularNode<InternalTreeMapNode>;
  highlightedItems?: LegendItem[];
  highlightingMap: HighlightingMap;
}

export const TreeMapCanvas = ({
  treeMapData,
  highlightedItems,
  highlightingMap,
}: TreeMapCanvasProps): JSX.Element => {
  const labelsDisplay = useLabelsDisplay();

  const nodes = useMemo(
    () => treeMapData.descendants().reverse(),
    [treeMapData],
  );
  const { clusters, leafs } = useMemo(() => splitNodesByDepth(nodes), [nodes]);

  useSetHierarchyNodes(clusters, leafs);

  const labelsToDisplay: string[] = [LABELS_DISPLAY.Nodes, LABELS_DISPLAY.All];
  const showLabel = labelsToDisplay.includes(labelsDisplay);

  const squares = leafs.map((node) => {
    const { x, y } = getNodePosition(node);
    const { width, height } = getNodeDimension(node);
    const color = getNodeColor(node);
    const opacity = getNodeOpacity(node, highlightingMap, highlightedItems);

    const shouldDraw = shouldDrawLeafNode(node);
    const hasParentLabels = withParentLabel(labelsDisplay);
    const skipDrawing = !shouldDraw && hasParentLabels;

    return (
      <TreeMapSquareRenderer
        x={x}
        y={y}
        width={width}
        height={height}
        color={color}
        opacity={opacity}
        name={node.data.name}
        value={node.data.formattedValue ?? ''}
        skipDrawing={skipDrawing}
        withLabel={showLabel}
        key={node.data.id}
      />
    );
  });

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{squares}</>;
};
